import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DeltaBadge } from './DeltaBadge';
import { EmptyState } from './EmptyState';
import { fmtISTDate } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Row {
  date: string;
  current: number;
  previous?: number;
}

interface Props {
  title: string;
  rows: Row[];
  loading?: boolean;
  format: (n: number) => string;
  comparisonLabel?: string;
}

export function DailyBreakdown({
  title,
  rows,
  loading,
  format,
  comparisonLabel = 'vs prior period day',
}: Props) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {title}
          <span className="text-2xs font-normal text-muted-foreground">{comparisonLabel}</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-6">
            <EmptyState title="No data" description="No daily rows for this period." />
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 border-b border-border/60 bg-muted/30 text-2xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Date</th>
                  <th className="px-4 py-2 text-right font-medium">Current</th>
                  <th className="px-4 py-2 text-right font-medium">Prior</th>
                  <th className="px-4 py-2 text-right font-medium">Δ%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {[...rows].reverse().map((r) => {
                  const prev = r.previous ?? 0;
                  const deltaPct =
                    prev === 0 ? 0 : ((r.current - prev) / Math.abs(prev)) * 100;
                  const comparable = !(prev === 0 && r.current !== 0);
                  return (
                    <tr key={r.date} className="hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono text-2xs text-muted-foreground">
                        {fmtISTDate(new Date(r.date))}
                      </td>
                      <td className={cn('px-4 py-2 text-right font-mono tabular-nums')}>
                        {format(r.current)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono tabular-nums text-muted-foreground">
                        {r.previous !== undefined ? format(prev) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {r.previous !== undefined ? (
                          <DeltaBadge deltaPct={deltaPct} comparable={comparable} />
                        ) : (
                          <span className="text-2xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
